/**
 * LocationMapper - Interactive tool for warehouse managers to map
 * location codes to floor plan coordinates by clicking.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { endpoints, API_URL } from '../config/api';

interface LocationMapperProps {
  warehouseCode: string;
  svgUrl?: string;
  onSave?: (mappings: LocationMapping[]) => void;
  onClose?: () => void;
}

export interface LocationMapping {
  location_id: number;
  location_code: string;
  location_name: string;
  x: number;
  y: number;
  mapped_by?: string;
  mapped_at?: string;
}

interface OdooLocation {
  id: number;
  name: string;
  complete_name: string;
}

// Known SVG paths
const WAREHOUSE_SVG_PATHS: Record<string, string> = {
  'IN': '/data/sites/in/converted/IN.svg',
  'MO': '/data/sites/mo/converted/MO.svg',
  'ID': '/data/sites/id/converted/ID Warehouse.svg',
  'GA-PM': '/data/sites/ga-pm/converted/GA Plant.svg',
  'GA': '/data/sites/ga-pm/converted/GA Plant.svg',
  'PA': '/data/sites/pa/converted/PA.svg',
};

export default function LocationMapper({
  warehouseCode,
  svgUrl,
  onSave,
  onClose,
}: LocationMapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });

  // Mapping state
  const [odooLocations, setOdooLocations] = useState<OdooLocation[]>([]);
  const [mappings, setMappings] = useState<LocationMapping[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<OdooLocation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // View state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState<'view' | 'map'>('map');
  const [importing, setImporting] = useState(false);

  // Load SVG
  useEffect(() => {
    const loadSvg = async () => {
      const url = svgUrl || WAREHOUSE_SVG_PATHS[warehouseCode.toUpperCase()];
      if (!url) {
        setError(`No floor plan for warehouse: ${warehouseCode}`);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to load floor plan');
        const text = await response.text();
        setSvgContent(text);

        // Extract dimensions from SVG
        const match = text.match(/viewBox="(\d+)\s+(\d+)\s+(\d+)\s+(\d+)"/);
        if (match) {
          setSvgDimensions({
            width: parseInt(match[3]),
            height: parseInt(match[4]),
          });
        }

        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
        setLoading(false);
      }
    };

    loadSvg();
  }, [warehouseCode, svgUrl]);

  // Load Odoo locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await fetch(
          endpoints.odooLocations(warehouseCode, 'production'),
          { credentials: 'include' }
        );
        if (!response.ok) return;

        const data = await response.json();
        // Filter to storage locations (those under Stock)
        const storageLocations = (data.locations || []).filter(
          (loc: OdooLocation) => loc.complete_name?.includes('/Stock/')
        );
        setOdooLocations(storageLocations);
      } catch (err) {
        console.error('Failed to load locations:', err);
      }
    };

    loadLocations();
  }, [warehouseCode]);

  // Load existing mappings
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/v1/warehouses/${warehouseCode}/location-mappings`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setMappings(data.mappings || []);
        }
      } catch (err) {
        // No existing mappings
      }
    };

    loadMappings();
  }, [warehouseCode]);

  // Filter locations by search
  const filteredLocations = odooLocations.filter(loc =>
    loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.complete_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Unmapped locations
  const unmappedLocations = filteredLocations.filter(
    loc => !mappings.find(m => m.location_id === loc.id)
  );

  // Handle click on floor plan
  const handleFloorPlanClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== 'map' || !selectedLocation) return;

    const container = containerRef.current;
    if (!container) return;

    const svgElement = container.querySelector('svg');
    if (!svgElement) return;

    // Get click position relative to SVG
    const svgRect = svgElement.getBoundingClientRect();
    const x = ((e.clientX - svgRect.left) / svgRect.width) * svgDimensions.width;
    const y = ((e.clientY - svgRect.top) / svgRect.height) * svgDimensions.height;

    // Add mapping
    const newMapping: LocationMapping = {
      location_id: selectedLocation.id,
      location_code: selectedLocation.name,
      location_name: selectedLocation.complete_name,
      x: Math.round(x),
      y: Math.round(y),
      mapped_at: new Date().toISOString(),
    };

    setMappings(prev => {
      // Remove existing mapping for this location if any
      const filtered = prev.filter(m => m.location_id !== selectedLocation.id);
      return [...filtered, newMapping];
    });

    // Clear selection and move to next unmapped
    setSelectedLocation(null);
  }, [mode, selectedLocation, svgDimensions]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode === 'view' && e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [mode, pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.min(Math.max(z * delta, 0.2), 5));
  }, []);

  // Save mappings
  const handleSave = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/v1/warehouses/${warehouseCode}/location-mappings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mappings }),
        }
      );

      if (response.ok) {
        onSave?.(mappings);
        alert(`Saved ${mappings.length} location mappings!`);
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      // Fallback: download as JSON
      const blob = new Blob([JSON.stringify(mappings, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${warehouseCode}_location_mappings.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [warehouseCode, mappings, onSave]);

  // Export to CSV
  const handleExportCSV = useCallback(() => {
    const csv = [
      'location_id,location_code,location_name,x,y,mapped_at',
      ...mappings.map(m =>
        `${m.location_id},"${m.location_code}","${m.location_name}",${m.x},${m.y},${m.mapped_at || ''}`
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${warehouseCode}_location_mappings.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [warehouseCode, mappings]);

  // Import from CSV
  const handleImportCSV = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const response = await fetch(
        `${API_URL}/api/v1/warehouses/${warehouseCode}/location-mappings/import`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/csv' },
          credentials: 'include',
          body: text,
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`Import complete: ${result.imported} new, ${result.updated} updated` +
              (result.errors?.length ? `\n${result.errors.length} errors` : ''));

        // Reload mappings
        const mappingsResp = await fetch(
          `${API_URL}/api/v1/warehouses/${warehouseCode}/location-mappings`,
          { credentials: 'include' }
        );
        if (mappingsResp.ok) {
          const data = await mappingsResp.json();
          setMappings(data.mappings || []);
        }
      } else {
        const err = await response.json();
        alert(`Import failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      // Parse CSV locally if API unavailable
      try {
        const text = await file.text();
        const lines = text.trim().split('\n');
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());

        const newMappings: LocationMapping[] = [];
        for (const line of lines.slice(1)) {
          if (!line.trim()) continue;
          const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row: Record<string, string> = {};
          header.forEach((h, i) => row[h] = values[i] || '');

          newMappings.push({
            location_id: parseInt(row.location_id),
            location_code: row.location_code,
            location_name: row.location_name || '',
            x: parseFloat(row.x),
            y: parseFloat(row.y),
            mapped_at: row.mapped_at || new Date().toISOString(),
          });
        }

        setMappings(prev => {
          const merged = [...prev];
          for (const m of newMappings) {
            const idx = merged.findIndex(x => x.location_id === m.location_id);
            if (idx >= 0) {
              merged[idx] = m;
            } else {
              merged.push(m);
            }
          }
          return merged;
        });
        alert(`Loaded ${newMappings.length} mappings from CSV (local)`);
      } catch (parseErr) {
        alert(`Import failed: ${parseErr instanceof Error ? parseErr.message : 'Parse error'}`);
      }
    } finally {
      setImporting(false);
      // Reset file input
      e.target.value = '';
    }
  }, [warehouseCode]);

  // Remove mapping
  const handleRemoveMapping = useCallback((locationId: number) => {
    setMappings(prev => prev.filter(m => m.location_id !== locationId));
  }, []);

  if (loading) {
    return (
      <div className="location-mapper">
        <div className="mapper-loading">
          <div className="loading-spinner" />
          <p>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="location-mapper">
        <div className="mapper-error">
          <p>{error}</p>
          {onClose && <button onClick={onClose}>Close</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="location-mapper">
      {/* Header */}
      <div className="mapper-header">
        <h3>
          Location Mapper - {warehouseCode}
          <span className="mapper-badge">{mappings.length} mapped</span>
          <span className="mapper-badge secondary">{unmappedLocations.length} remaining</span>
        </h3>
        <div className="mapper-actions">
          <button
            className={`mapper-btn ${mode === 'map' ? 'active' : ''}`}
            onClick={() => setMode('map')}
          >
            Map Mode
          </button>
          <button
            className={`mapper-btn ${mode === 'view' ? 'active' : ''}`}
            onClick={() => setMode('view')}
          >
            View Mode
          </button>
          <label className="mapper-btn" style={{ cursor: importing ? 'wait' : 'pointer' }}>
            {importing ? 'Importing...' : 'Import CSV'}
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportCSV}
              disabled={importing}
            />
          </label>
          <button className="mapper-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
          <button className="mapper-btn primary" onClick={handleSave}>
            Save
          </button>
          {onClose && (
            <button className="mapper-close" onClick={onClose}>×</button>
          )}
        </div>
      </div>

      {/* Instructions */}
      {mode === 'map' && (
        <div className="mapper-instructions">
          {selectedLocation ? (
            <p>
              Click on the floor plan to place <strong>{selectedLocation.name}</strong>
            </p>
          ) : (
            <p>Select a location from the list, then click on the floor plan to mark its position</p>
          )}
        </div>
      )}

      <div className="mapper-main">
        {/* Floor Plan */}
        <div
          className="mapper-floor-plan"
          ref={containerRef}
          onClick={handleFloorPlanClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ cursor: mode === 'map' && selectedLocation ? 'crosshair' : 'grab' }}
        >
          <div
            className="mapper-transform"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
          >
            {svgContent && (
              <div
                className="mapper-svg"
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            )}

            {/* Mapped location markers */}
            <svg
              className="mapper-overlay"
              viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            >
              {mappings.map(m => (
                <g key={m.location_id}>
                  <circle
                    cx={m.x}
                    cy={m.y}
                    r={200}
                    fill="rgba(74, 144, 217, 0.8)"
                    stroke="white"
                    strokeWidth={40}
                  />
                  <text
                    x={m.x}
                    y={m.y + 80}
                    textAnchor="middle"
                    fill="white"
                    fontSize={180}
                    fontWeight="bold"
                  >
                    {m.location_code}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Sidebar */}
        <div className="mapper-sidebar">
          <div className="mapper-search">
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="mapper-tabs">
            <button className="mapper-tab active">
              Unmapped ({unmappedLocations.length})
            </button>
          </div>

          <div className="mapper-location-list">
            {unmappedLocations.slice(0, 50).map(loc => (
              <div
                key={loc.id}
                className={`mapper-location-item ${selectedLocation?.id === loc.id ? 'selected' : ''}`}
                onClick={() => setSelectedLocation(loc)}
              >
                <span className="mapper-location-code">{loc.name}</span>
                <span className="mapper-location-path">
                  {loc.complete_name.split('/').slice(-2).join('/')}
                </span>
              </div>
            ))}
          </div>

          <h5>Mapped Locations</h5>
          <div className="mapper-mapped-list">
            {mappings.slice(0, 20).map(m => (
              <div key={m.location_id} className="mapper-mapped-item">
                <span>{m.location_code}</span>
                <span className="mapper-coords">({m.x}, {m.y})</span>
                <button
                  className="mapper-remove"
                  onClick={() => handleRemoveMapping(m.location_id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .location-mapper {
          display: flex;
          flex-direction: column;
          height: 100vh;
          background: #0a0a1a;
          color: #e0e0e0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .mapper-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #1a1a2e;
          border-bottom: 1px solid #333;
        }

        .mapper-header h3 {
          margin: 0;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mapper-badge {
          background: #4a90d9;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: normal;
        }

        .mapper-badge.secondary {
          background: #666;
        }

        .mapper-actions {
          display: flex;
          gap: 8px;
        }

        .mapper-btn {
          padding: 6px 12px;
          border: 1px solid #444;
          background: #2a2a3e;
          color: #e0e0e0;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
        }

        .mapper-btn:hover, .mapper-btn.active {
          background: #3a3a5e;
        }

        .mapper-btn.primary {
          background: #4a90d9;
          border-color: #4a90d9;
        }

        .mapper-close {
          background: transparent;
          border: none;
          color: #888;
          font-size: 24px;
          cursor: pointer;
        }

        .mapper-instructions {
          padding: 8px 16px;
          background: #2a2a3e;
          border-bottom: 1px solid #333;
          font-size: 13px;
        }

        .mapper-instructions strong {
          color: #4a90d9;
        }

        .mapper-main {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .mapper-floor-plan {
          flex: 1;
          overflow: hidden;
          position: relative;
          background: #12121f;
        }

        .mapper-transform {
          transform-origin: center center;
          position: relative;
        }

        .mapper-svg {
          display: flex;
          justify-content: center;
        }

        .mapper-svg svg {
          background: white;
        }

        .mapper-sidebar {
          width: 300px;
          background: #1a1a2e;
          border-left: 1px solid #333;
          display: flex;
          flex-direction: column;
        }

        .mapper-search {
          padding: 12px;
          border-bottom: 1px solid #333;
        }

        .mapper-search input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #444;
          background: #2a2a3e;
          color: #e0e0e0;
          border-radius: 4px;
          font-size: 13px;
        }

        .mapper-tabs {
          display: flex;
          border-bottom: 1px solid #333;
        }

        .mapper-tab {
          flex: 1;
          padding: 10px;
          background: transparent;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 12px;
        }

        .mapper-tab.active {
          color: #4a90d9;
          border-bottom: 2px solid #4a90d9;
        }

        .mapper-location-list {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }

        .mapper-location-item {
          padding: 8px 10px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .mapper-location-item:hover {
          background: #2a2a3e;
        }

        .mapper-location-item.selected {
          background: #3a3a5e;
          border: 1px solid #4a90d9;
        }

        .mapper-location-code {
          font-weight: 600;
          font-size: 13px;
        }

        .mapper-location-path {
          font-size: 10px;
          color: #888;
        }

        .mapper-sidebar h5 {
          margin: 0;
          padding: 12px;
          font-size: 12px;
          color: #888;
          text-transform: uppercase;
          border-top: 1px solid #333;
        }

        .mapper-mapped-list {
          max-height: 200px;
          overflow-y: auto;
          padding: 0 8px 8px;
        }

        .mapper-mapped-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          font-size: 12px;
          background: #2a2a3e;
          border-radius: 4px;
          margin-bottom: 4px;
        }

        .mapper-coords {
          color: #888;
          font-size: 10px;
          flex: 1;
        }

        .mapper-remove {
          background: transparent;
          border: none;
          color: #f44336;
          cursor: pointer;
          font-size: 16px;
          padding: 0 4px;
        }

        .mapper-loading, .mapper-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          gap: 16px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #333;
          border-top-color: #4a90d9;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
